import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, RefreshCw, Clock, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { Reservation } from '../../types';
import apiService from '../../services/api';
import ReservationCard from './ReservationCard';
import { useRestaurant } from '../../contexts/RestaurantContext';

const TodayReservations: React.FC = () => {
  const { restaurant } = useRestaurant();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTodayReservations = useCallback(async () => {
    if (!restaurant?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];
      const data = await apiService.getReservationsByRestaurantAndDate(restaurant.id, today);
      
      // Ensure data is an array before filtering and sorting
      if (Array.isArray(data)) {
        // Filter out canceled reservations and those with failed payments
        const filteredReservations = data.filter(reservation => 
          reservation.status !== 'cancelada' && 
          reservation.payment_status !== 'failed'
        );
        
        // Sort by time
        const sortedReservations = filteredReservations.sort((a, b) => 
          new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime()
        );
        setReservations(sortedReservations);
      } else {
        setReservations([]);
      }
    } catch (error) {
      console.error('Failed to fetch today\'s reservations:', error);
      setReservations([]); // Ensure it's always an empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => {
    if (restaurant?.id) {
      fetchTodayReservations();
      
      // Auto-refresh every 5 minutes
      const interval = setInterval(fetchTodayReservations, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [restaurant?.id, fetchTodayReservations]);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await apiService.updateReservationStatus(id, { status: status as any });
      await fetchTodayReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to update reservation status:', error);
    }
  };

  const handleCancelReservation = async (id: number) => {
    try {
      await apiService.cancelReservation(id);
      await fetchTodayReservations(); // Refresh the list
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
    }
  };

  const handleRefundReservation = async (id: number) => {
    try {
      await fetchTodayReservations(); // Refresh the list to show updated payment status
    } catch (error) {
      console.error('Failed to refresh reservations after refund:', error);
    }
  };

  // Note: reservations array is already filtered (no canceled or failed payment)
  const todayStats = {
    total: Array.isArray(reservations) ? reservations.length : 0,
    confirmed: Array.isArray(reservations) ? reservations.filter(r => r.status === 'confirmada').length : 0,
    pending: Array.isArray(reservations) ? reservations.filter(r => r.status === 'pendente').length : 0,
    completed: Array.isArray(reservations) ? reservations.filter(r => r.status === 'concluida').length : 0,
    totalGuests: Array.isArray(reservations) ? reservations.reduce((sum, r) => sum + r.numero_pessoas, 0) : 0,
  };

  const getNextReservation = () => {
    if (!Array.isArray(reservations)) return null;
    const now = new Date();
    return reservations.find(r => {
      const reservationTime = new Date(r.data_hora);
      return reservationTime > now && (r.status === 'confirmada' || r.status === 'pendente');
    });
  };

  const nextReservation = getNextReservation();

  const formatTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleTimeString('pt-PT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const isUpcoming = (dateTime: string) => {
    const now = new Date();
    const reservationTime = new Date(dateTime);
    const diffInMinutes = (reservationTime.getTime() - now.getTime()) / (1000 * 60);
    return diffInMinutes > 0 && diffInMinutes <= 60; // Next hour
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Reservas de Hoje
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            {new Date().toLocaleDateString('pt-PT', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
          <button
            onClick={fetchTodayReservations}
            disabled={isLoading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - Showing only active reservations (excluding canceled and failed payments) */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <div className="card p-4">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-secondary-900">{todayStats.total}</p>
              <p className="text-xs text-secondary-600">Total Ativas</p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-secondary-900">{todayStats.confirmed}</p>
              <p className="text-xs text-secondary-600">Confirmadas</p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-secondary-900">{todayStats.pending}</p>
              <p className="text-xs text-secondary-600">Pendentes</p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-500" />
            <div className="ml-3">
              <p className="text-2xl font-semibold text-secondary-900">{todayStats.totalGuests}</p>
              <p className="text-xs text-secondary-600">Pessoas</p>
            </div>
          </div>
        </div>
        
        <div className="card p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-500" />
            <div className="ml-3">
              <p className="text-lg font-semibold text-secondary-900">
                {nextReservation ? formatTime(nextReservation.data_hora) : '--:--'}
              </p>
              <p className="text-xs text-secondary-600">Próxima</p>
            </div>
          </div>
        </div>
      </div>

      {/* Next Reservation Alert */}
      {nextReservation && isUpcoming(nextReservation.data_hora) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-center">
            <Clock className="h-5 w-5 text-yellow-600" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Próxima Reserva em Breve
              </h3>
              <p className="text-sm text-yellow-700">
                <strong>{nextReservation.nome_cliente}</strong> - {formatTime(nextReservation.data_hora)} 
                - {nextReservation.numero_pessoas} pessoa{nextReservation.numero_pessoas !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Reservations List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
            <p className="mt-2 text-sm text-secondary-600">A carregar reservas de hoje...</p>
          </div>
        </div>
      ) : !Array.isArray(reservations) || reservations.length === 0 ? (
        <div className="card p-6 text-center">
          <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            Nenhuma reserva para hoje
          </h3>
          <p className="text-secondary-500">
            Não há reservas agendadas para o dia de hoje.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-secondary-900">
            Todas as Reservas ({Array.isArray(reservations) ? reservations.length : 0})
          </h2>
          {Array.isArray(reservations) && reservations.map((reservation, index) => (
            <div key={reservation.id} className="relative">
              {/* Time separator */}
              {(index === 0 || formatTime(reservation.data_hora) !== formatTime(reservations[index - 1].data_hora)) && (
                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-secondary-200"></div>
                  <div className="px-3 text-sm font-medium text-secondary-500 bg-secondary-50 rounded">
                    {formatTime(reservation.data_hora)}
                  </div>
                  <div className="flex-1 border-t border-secondary-200"></div>
                </div>
              )}
              
              {/* Highlight upcoming reservations */}
              <div className={isUpcoming(reservation.data_hora) ? 'ring-2 ring-yellow-300 rounded-lg' : ''}>
                <ReservationCard
                  reservation={reservation}
                  onStatusChange={handleStatusChange}
                  onCancel={handleCancelReservation}
                  onRefund={handleRefundReservation}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TodayReservations;
